// File: src/components/BallooningViewerKonva.tsx
import React, {
	useState,
	useRef,
	useEffect,
	useCallback,
	useMemo,
	SetStateAction,
	Dispatch,
} from 'react';
import Konva from 'konva';

import { useParams } from 'react-router';
import {
	Stage,
	Layer,
	Image as KonvaImage,
	Rect,
	Text,
	Group,
	Line,
} from 'react-konva';
import { Card, Modal, Select, Button, Flex, Row, Col, Typography, Menu } from 'antd';
import { CaretRightOutlined, CaretLeftOutlined, DeleteOutlined } from '@ant-design/icons';
import Loading from '../../components/Loading';
import { useDispatch } from 'react-redux';
import { useGetBallooningProjectQuery } from '../../redux/api/ballooning';
import BallooningPages from '../BallooningPages';
import { Balloon, ViewOutline } from '../../interfaces/Ballooning';
import {
	boxToDisplayed,
	imageToDisplayed,
	nearestPointOnBox,
	resolveReferenceDimensions,
} from './coords';
import { isUnreviewedAutoBalloon } from '../../utils/balloonReview';

const UNREVIEWED_STROKE = '#fa8c16';

const { Option } = Select;

export type ImageLoadState = 'idle' | 'loading' | 'loaded' | 'error' | 'missing';

interface DisplayedDimensions {
	width: number;
	height: number;
	offsetX: number;
	offsetY: number;
}

interface BallooningViewerProps {
	editable: boolean;
	activeBalloonId: string | null;
	setActiveBalloonId: (id: string | null) => void;
	centerOnActive: boolean;
	setCenterOnActive: (flag: boolean) => void;
	localBalloons: Balloon[];
	/**
	 * Show the page's saved balloons when `localBalloons` has none on this page.
	 * Editors that own the balloon list must pass `false`, or deleting the last
	 * balloon on a page brings the saved ones back into view.
	 */
	fallbackToSavedBalloons?: boolean;
	setLocalBalloons: Dispatch<SetStateAction<Balloon[]>>;
	setDeletedBalloonIds?: Dispatch<SetStateAction<string[]>>;
	balloonSize: number;
	pageID: string;
	pageNumber: number | null;
	setPageNumber: Dispatch<SetStateAction<number>>;
	fitMode?: 'height' | 'width' | 'auto';
	showLeftPageBar?: boolean;
	projectId?: string;
	isTabletView: boolean;
	disableDragging?: boolean;
	allowedEditableBalloonId?: string | null;
	/** When set, overrides the default Keyence/non-Keyence stroke colour per balloon. */
	getBalloonStrokeColor?: (balloon: Balloon) => string | undefined;
	/** Ballooning editor only: highlight unreviewed AI balloons and outline detected features. */
	showReviewAids?: boolean;
	/** When set, clicking a balloon opens this content in a card anchored next to it. */
	renderBalloonPopup?: (balloon: Balloon, close: () => void) => React.ReactNode;
	/**
	 * Enables the right-click menu on balloons that are past review (unreviewed AI
	 * balloons are handled in the review flow instead).
	 */
	onDeleteBalloon?: (balloon: Balloon) => void;
	/** Draw the traced outline of each drawing view (page.view_outlines). */
	showViewOutlines?: boolean;
	/** Draw a line from every balloon to its feature_bbox, in the balloon's colour. */
	showLeaderLines?: boolean;
	/** Reports whether the current page's drawing image loaded. */
	onImageLoadStateChange?: (state: ImageLoadState) => void;
}

const VIEW_OUTLINE_COLORS = ['#13c2c2', '#722ed1', '#eb2f96', '#52c41a', '#fa541c', '#2f54eb'];

// A right-drag pans the drawing; only a press that barely moves opens the menu.
const CONTEXT_MENU_MAX_MOVE = 4;
const CONTEXT_MENU_WIDTH = 180;
const CONTEXT_MENU_HEIGHT = 48;

const POPUP_WIDTH = 288;
const POPUP_ESTIMATED_HEIGHT = 230;
const POPUP_GAP = 10;

const renumberBalloons = (balloons: Balloon[]) =>
	balloons.map((balloon, index) => ({ ...balloon, balloon_number: index + 1 }));

const BallooningViewer: React.FC<BallooningViewerProps> = ({
	editable,
	activeBalloonId,
	setActiveBalloonId,
	centerOnActive,
	setCenterOnActive,
	localBalloons,
	fallbackToSavedBalloons = true,
	setLocalBalloons,
	setDeletedBalloonIds,
	balloonSize,
	pageID,
	pageNumber,
	setPageNumber,
	fitMode = 'width',
	showLeftPageBar = true,
	projectId: propProjectId,
	isTabletView,
	disableDragging = false,
	allowedEditableBalloonId,
	getBalloonStrokeColor,
	showReviewAids = false,
	renderBalloonPopup,
	onDeleteBalloon,
	showViewOutlines = false,
	showLeaderLines = false,
	onImageLoadStateChange,
}) => {
	// ...
	// ... in Group ...

	const { id } = useParams();
	const projectId = propProjectId || id;

	const dispatch = useDispatch();
	const containerRef = useRef<HTMLDivElement>(null);
	const drawingRef = useRef<HTMLDivElement>(null);
	const stageRef = useRef<Konva.Stage>(null);

	// Global pan/zoom state.
	const [stageScale, setStageScale] = useState(1);
	const [stageOffset, setStageOffset] = useState({ x: 0, y: 0 });
	const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
	const [hoveredBalloonId, setHoveredBalloonId] = useState<string | null>(null);
	// Live center, in layer coordinates, of the balloon being dragged.
	const [dragCenter, setDragCenter] = useState<{ id: string; x: number; y: number } | null>(
		null
	);
	const [popupBalloonId, setPopupBalloonId] = useState<string | null>(null);
	const closePopup = useCallback(() => setPopupBalloonId(null), []);

	// Calculated dimensions for the image.
	const [displayedDimensions, setDisplayedDimensions] =
		useState<DisplayedDimensions>({
			width: 0,
			height: 0,
			offsetX: 0,
			offsetY: 0,
		});

	// Load project data.
	const getBallooningProject = useGetBallooningProjectQuery(
		{ id: projectId as string },
		{ skip: !projectId }
	);

	const ballooningPages = getBallooningProject.data?.ballooning_pages || [];

	const currentPage = ballooningPages.find(
		(page) => page.page_number === pageNumber
	);

	const pageExists = !!currentPage;

	const imageUrl = currentPage?.redacted_drawing_page || '';
	const apiImageWidth = currentPage?.get_image_dimensions?.[0] ?? 0;
	const apiImageHeight = currentPage?.get_image_dimensions?.[1] ?? 0;

	// Load image.
	const [konvaImage, setKonvaImage] = useState<HTMLImageElement | null>(null);
	const [imageNaturalWidth, setImageNaturalWidth] = useState(0);
	const [imageNaturalHeight, setImageNaturalHeight] = useState(0);
	const [imageLoadState, setImageLoadState] =
		useState<ImageLoadState>('idle');

	const pageBalloons = useMemo(() => {
		const fromLocal = localBalloons.filter(
			(balloon) => balloon.ballooning_page_number === pageNumber
		);
		if (fromLocal.length > 0 || !fallbackToSavedBalloons) {
			return fromLocal;
		}
		return currentPage?.balloons ?? [];
	}, [localBalloons, pageNumber, currentPage?.balloons, fallbackToSavedBalloons]);

	const { width: referenceWidth, height: referenceHeight } = useMemo(
		() =>
			resolveReferenceDimensions(
				apiImageWidth,
				apiImageHeight,
				imageNaturalWidth,
				imageNaturalHeight,
				pageBalloons
			),
		[
			apiImageWidth,
			apiImageHeight,
			imageNaturalWidth,
			imageNaturalHeight,
			pageBalloons,
		]
	);

	const layoutWidth = referenceWidth;
	const layoutHeight = referenceHeight;
	const imageUnavailable =
		imageLoadState === 'error' || imageLoadState === 'missing';
	const canRenderStage =
		imageLoadState === 'loaded' ||
		(imageUnavailable && layoutWidth > 0 && layoutHeight > 0);

	useEffect(() => {
		if (!imageUrl) {
			setKonvaImage(null);
			setImageNaturalWidth(0);
			setImageNaturalHeight(0);
			setImageLoadState(pageExists ? 'missing' : 'idle');
			return;
		}

		let cancelled = false;
		setImageLoadState('loading');
		setKonvaImage(null);
		setImageNaturalWidth(0);
		setImageNaturalHeight(0);

		const img = new window.Image();
		img.onload = () => {
			if (cancelled) {
				return;
			}
			setKonvaImage(img);
			setImageNaturalWidth(img.naturalWidth || img.width);
			setImageNaturalHeight(img.naturalHeight || img.height);
			setImageLoadState('loaded');
		};
		img.onerror = () => {
			if (cancelled) {
				return;
			}
			setKonvaImage(null);
			setImageNaturalWidth(0);
			setImageNaturalHeight(0);
			setImageLoadState('error');
		};
		img.src = imageUrl;

		return () => {
			cancelled = true;
			img.onload = null;
			img.onerror = null;
		};
	}, [imageUrl, pageExists]);

	useEffect(() => {
		onImageLoadStateChange?.(imageLoadState);
	}, [imageLoadState, onImageLoadStateChange]);

	// Use ResizeObserver to update stage size.
	useEffect(() => {
		const updateStageSize = () => {
			if (drawingRef.current) {
				setStageSize({
					width: drawingRef.current.clientWidth,
					height: drawingRef.current.clientHeight,
				});
			}
		};
		updateStageSize();
		let resizeObserver: ResizeObserver | null = null;
		if (drawingRef.current && typeof ResizeObserver !== 'undefined') {
			resizeObserver = new ResizeObserver(() => updateStageSize());
			resizeObserver.observe(drawingRef.current);
		} else {
			window.addEventListener('resize', updateStageSize);
		}
		return () => {
			if (resizeObserver) {
				resizeObserver.disconnect();
			} else {
				window.removeEventListener('resize', updateStageSize);
			}
		};
	}, []);

	useEffect(() => {
		if (
			stageSize.width &&
			stageSize.height &&
			layoutWidth &&
			layoutHeight
		) {
			let scaleFactor: number;

			switch (fitMode) {
				case 'height':
					scaleFactor = stageSize.height / layoutHeight;
					break;
				case 'width':
					scaleFactor = stageSize.width / layoutWidth;
					break;
				case 'auto':
				default:
					scaleFactor = Math.max(
						stageSize.width / layoutWidth,
						stageSize.height / layoutHeight
					);
					break;
			}

			const displayedWidth = layoutWidth * scaleFactor;
			const displayedHeight = layoutHeight * scaleFactor;
			const offsetX = (stageSize.width - displayedWidth) / 2;
			const offsetY = (stageSize.height - displayedHeight) / 2;
			setDisplayedDimensions({
				width: displayedWidth,
				height: displayedHeight,
				offsetX,
				offsetY,
			});
		}
	}, [stageSize, layoutWidth, layoutHeight, fitMode]);

	// Zoom handler.
	const handleWheel = useCallback(
		(e: any) => {
			e.evt.preventDefault();
			const scaleBy = 1.05;
			const oldScale = stageScale;
			const pointer = e.target.getStage().getPointerPosition();
			if (!pointer) return;
			const mousePointTo = {
				x: (pointer.x - stageOffset.x) / oldScale,
				y: (pointer.y - stageOffset.y) / oldScale,
			};
			const direction = e.evt.deltaY > 0 ? -1 : 1;
			const newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;
			setStageScale(newScale);
			setStageOffset({
				x: pointer.x - mousePointTo.x * newScale,
				y: pointer.y - mousePointTo.y * newScale,
			});
		},
		[stageScale, stageOffset]
	);

	// Panning (for right-click drag).
	const isPanning = useRef(false);
	const lastPanPos = useRef({ x: 0, y: 0 });
	const rightPressAt = useRef<{ x: number; y: number } | null>(null);
	const [contextMenu, setContextMenu] = useState<{
		balloonId: string;
		left: number;
		top: number;
	} | null>(null);

	const canEditBalloon = (balloon: Balloon) =>
		editable && (!allowedEditableBalloonId || allowedEditableBalloonId === balloon.id);

	const handleBalloonContextMenu = (
		balloon: Balloon,
		e: Konva.KonvaEventObject<PointerEvent>
	) => {
		e.evt.preventDefault();
		const press = rightPressAt.current;
		const moved =
			!press ||
			Math.hypot(e.evt.clientX - press.x, e.evt.clientY - press.y) > CONTEXT_MENU_MAX_MOVE;
		if (
			moved ||
			!onDeleteBalloon ||
			!canEditBalloon(balloon) ||
			(showReviewAids && isUnreviewedAutoBalloon(balloon))
		) {
			return;
		}
		const rect = drawingRef.current?.getBoundingClientRect();
		if (!rect) return;
		e.cancelBubble = true;
		setPopupBalloonId(null);
		setActiveBalloonId(balloon.id);
		setContextMenu({
			balloonId: balloon.id,
			left: Math.min(e.evt.clientX - rect.left, rect.width - CONTEXT_MENU_WIDTH),
			top: Math.min(e.evt.clientY - rect.top, rect.height - CONTEXT_MENU_HEIGHT),
		});
	};

	useEffect(() => {
		if (!contextMenu) return;
		const close = () => setContextMenu(null);
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') close();
		};
		window.addEventListener('keydown', handleKeyDown);
		window.addEventListener('mousedown', close);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('mousedown', close);
		};
	}, [contextMenu]);

	useEffect(() => {
		setContextMenu(null);
	}, [pageNumber, stageScale, stageOffset]);
	const handleStageMouseDown = (e: any) => {
		setContextMenu(null);
		if (e.evt.button === 2) {
			isPanning.current = true;
			lastPanPos.current = { x: e.evt.clientX, y: e.evt.clientY };
			rightPressAt.current = { x: e.evt.clientX, y: e.evt.clientY };
		}
		// A click off the balloon dismisses its popup rather than dropping a new balloon.
		if (popupBalloonId && e.evt.button === 0 && e.target.id() === 'konvaImage') {
			setPopupBalloonId(null);
			return;
		}
		// If editable, left-click on image creates a new balloon.
		if (editable && e.evt.button === 0 && e.target.id() === 'konvaImage') {
			const pointer = e.target.getStage().getPointerPosition();
			if (!pointer) return;
			const unscaled = {
				x: (pointer.x - stageOffset.x) / stageScale,
				y: (pointer.y - stageOffset.y) / stageScale,
			};
			const adjustedX =
				((unscaled.x - displayedDimensions.offsetX) /
					displayedDimensions.width) *
				referenceWidth;
			const adjustedY =
				((unscaled.y - displayedDimensions.offsetY) /
					displayedDimensions.height) *
				referenceHeight;
			const newBalloon: Balloon = {
				// Unique temp id: `temp-${Date.now()}` alone collides when balloons are added within the
				// same millisecond (or in a batch), and updateBalloonField() writes to every balloon
				// sharing an id — so an edit to one would broadcast onto the others. Match the hardened
				// pattern already used in BalloonTable (random suffix).
				id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
				balloon_number: localBalloons.reduce((max, b) => Math.max(max, b.balloon_number), 0) + 1,
				coordinate_x: Math.round(adjustedX),
				coordinate_y: Math.round(adjustedY),
				ballooning_page_number: pageNumber || 1,
				ballooning_page: pageID,
				view: '',
				text_value: '',
				gage_type: '',
				measurement_type: '',
				measurement_strategy: '',
				nominal: '',
				tolerance_plus: '',
				tolerance_minus: '',
				lower_control_limit: '',
				upper_control_limit: '',
				gage_type_name: '',
				gage_type_is_pass_fail_gage: false,
			};
			setActiveBalloonId(newBalloon.id);
			setLocalBalloons([...localBalloons, newBalloon]);
		}
	};
	const handleStageMouseMove = (e: any) => {
		if (isPanning.current) {
			const dx = e.evt.clientX - lastPanPos.current.x;
			const dy = e.evt.clientY - lastPanPos.current.y;
			setStageOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
			lastPanPos.current = { x: e.evt.clientX, y: e.evt.clientY };
		}
	};
	const handleStageMouseUp = () => {
		isPanning.current = false;
	};

	// Utility: get canvas element.
	const getCanvasElement = () =>
		containerRef.current?.querySelector('canvas') as HTMLCanvasElement | null;
	const handleContextMenu = (e: any) => {
		e.evt.preventDefault();
	};

	// Auto-pan during balloon drag (only when dragging a balloon and if editable).
	const handleBalloonDragMove = (e: any) => {
		if (!editable) return;
		const pointer = stageRef.current?.getPointerPosition();
		if (!pointer || !stageSize.width || !stageSize.height) return;
		const threshold = 50; // pixels
		let deltaX = 0;
		let deltaY = 0;
		if (pointer.x < threshold) {
			deltaX = (threshold - pointer.x) * 0.2;
		} else if (pointer.x > stageSize.width - threshold) {
			deltaX = (stageSize.width - threshold - pointer.x) * 0.2;
		}
		if (pointer.y < threshold) {
			deltaY = (threshold - pointer.y) * 0.2;
		} else if (pointer.y > stageSize.height - threshold) {
			deltaY = (stageSize.height - threshold - pointer.y) * 0.2;
		}
		if (deltaX !== 0 || deltaY !== 0) {
			setStageOffset((prev) => ({ x: prev.x + deltaX, y: prev.y + deltaY }));
		}
	};

	// When a balloon is dragged, update its coordinates.
	const handleCircleDragEnd = (balloon: Balloon, e: any) => {
		// e.target.absolutePosition() returns the Group's position, which is the balloon's center.
		const newCenter = e.target.absolutePosition();
		const absPos = {
			x: (newCenter.x - stageOffset.x) / stageScale,
			y: (newCenter.y - stageOffset.y) / stageScale,
		};
		const newCoordinateX =
			((absPos.x - displayedDimensions.offsetX) / displayedDimensions.width) *
			referenceWidth;
		const newCoordinateY =
			((absPos.y - displayedDimensions.offsetY) / displayedDimensions.height) *
			referenceHeight;
		setLocalBalloons(
			localBalloons.map((b) =>
				b.id === balloon.id
					? {
						...b,
						coordinate_x: Math.round(newCoordinateX),
						coordinate_y: Math.round(newCoordinateY),
					}
					: b
			)
		);
		const canvas = getCanvasElement();
		if (canvas) canvas.style.cursor = 'default';
	};

	const activeBalloonLoaded = localBalloons.some((b) => b.id === activeBalloonId);

	// Smoothly center on the active balloon. The request stays pending until the balloon,
	// the page image and the stage are all measured (e.g. right after generation).
	useEffect(() => {
		if (!centerOnActive || !activeBalloonId || !stageRef.current) return;

		const balloon = localBalloons.find((b) => b.id === activeBalloonId);
		if (!balloon || !referenceWidth || !referenceHeight) return;
		if (!stageSize.width || !stageSize.height || !displayedDimensions.width) return;

		// 1. Zoom to 1.5x (or keep current if already zoomed in more)
		const zoomTarget = Math.max(stageScale, 1.5);

		// 2. Calculate the target position at the new zoom level
		const targetX =
			((balloon.coordinate_x / referenceWidth) * displayedDimensions.width +
				displayedDimensions.offsetX) *
			zoomTarget;

		const targetY =
			((balloon.coordinate_y / referenceHeight) *
				displayedDimensions.height +
				displayedDimensions.offsetY) *
			zoomTarget;

		// 3. Calculate the new offset to center the balloon
		const newOffsetX = stageSize.width / 2 - targetX;
		const newOffsetY = stageSize.height / 2 - targetY;

		// 4. Animation with pan + zoom
		new Konva.Tween({
			node: stageRef.current,
			duration: 0.5,
			x: newOffsetX,
			y: newOffsetY,
			scaleX: zoomTarget,
			scaleY: zoomTarget,
			onFinish: () => {
				setStageOffset({ x: newOffsetX, y: newOffsetY });
				setStageScale(zoomTarget);
			},
			easing: Konva.Easings.EaseInOut,
		}).play();

		setCenterOnActive(false);
	}, [
		centerOnActive,
		activeBalloonId,
		activeBalloonLoaded,
		referenceWidth,
		referenceHeight,
		displayedDimensions.width,
		displayedDimensions.height,
		stageSize.width,
		stageSize.height,
	]);

	// Listen for Delete key presses to remove the active balloon (only if editable).
	const [confirmModalVisible, setConfirmModalVisible] = useState(false);
	const [pendingDeletionRecord, setPendingDeletionRecord] =
		useState<Balloon | null>(null);
	const performDeletion = (record: Balloon, renumber: boolean) => {
		setLocalBalloons((prev) => {
			const filtered = prev.filter((b) => b.id !== record.id);
			return renumber ? renumberBalloons(filtered) : filtered;
		});
		if (setDeletedBalloonIds && !record.id.toString().startsWith('temp-')) {
			setDeletedBalloonIds((prev: any) => [...prev, record.id]);
		}
	};

	const handleModalOk = () => {
		if (pendingDeletionRecord) {
			performDeletion(pendingDeletionRecord, true);
		}
		setConfirmModalVisible(false);
		setPendingDeletionRecord(null);
		setActiveBalloonId(null);
	};
	const handleModalCancel = () => {
		if (pendingDeletionRecord) {
			performDeletion(pendingDeletionRecord, false);
		}
		setConfirmModalVisible(false);
		setPendingDeletionRecord(null);
		setActiveBalloonId(null);
	};

	// Compute balloon (circle) size.

	const letterboxScale = referenceWidth
		? displayedDimensions.width / referenceWidth
		: 0;
	const computedCircleSize = balloonSize * letterboxScale;
	const computedStrokeWidth = computedCircleSize * 0.1;

	const featureOverlay = useMemo(() => {
		if (!showReviewAids) return null;
		const targetId = dragCenter?.id ?? hoveredBalloonId ?? activeBalloonId;
		const balloon = pageBalloons.find((b: Balloon) => b.id === targetId);
		const bbox = balloon?.feature_bbox;
		if (!balloon || !bbox || !referenceWidth || !referenceHeight) return null;
		const meta = {
			referenceWidth,
			referenceHeight,
			displayedWidth: displayedDimensions.width,
			displayedHeight: displayedDimensions.height,
			offsetX: displayedDimensions.offsetX,
			offsetY: displayedDimensions.offsetY,
		};
		const box = boxToDisplayed(bbox, meta);
		const drag = dragCenter;
		const center =
			drag && drag.id === balloon.id
				? { x: drag.x, y: drag.y }
				: {
					x: meta.offsetX + (balloon.coordinate_x / referenceWidth) * meta.displayedWidth,
					y: meta.offsetY + (balloon.coordinate_y / referenceHeight) * meta.displayedHeight,
				};
		const anchor = nearestPointOnBox(center, box);
		const color = isUnreviewedAutoBalloon(balloon) ? UNREVIEWED_STROKE : '#1677ff';
		return { box, center, anchor, color };
	}, [
		showReviewAids,
		dragCenter,
		hoveredBalloonId,
		activeBalloonId,
		pageBalloons,
		referenceWidth,
		referenceHeight,
		displayedDimensions,
	]);

	const balloonStrokeColor = (balloon: Balloon) =>
		getBalloonStrokeColor?.(balloon) ??
		(showReviewAids && isUnreviewedAutoBalloon(balloon)
			? UNREVIEWED_STROKE
			: balloon.measurement_strategy === 'Keyence'
				? 'blue'
				: 'red');

	const leaderLines: { id: string; points: number[]; color: string }[] =
		showLeaderLines && referenceWidth && referenceHeight
			? pageBalloons.flatMap((balloon: Balloon) => {
				if (!balloon.feature_bbox) return [];
				const meta = {
					referenceWidth,
					referenceHeight,
					displayedWidth: displayedDimensions.width,
					displayedHeight: displayedDimensions.height,
					offsetX: displayedDimensions.offsetX,
					offsetY: displayedDimensions.offsetY,
				};
				const center =
					dragCenter?.id === balloon.id
						? { x: dragCenter.x, y: dragCenter.y }
						: imageToDisplayed({ x: balloon.coordinate_x, y: balloon.coordinate_y }, meta);
				const anchor = nearestPointOnBox(center, boxToDisplayed(balloon.feature_bbox, meta));
				return [
					{
						id: balloon.id,
						points: [center.x, center.y, anchor.x, anchor.y],
						color: balloonStrokeColor(balloon),
					},
				];
			})
			: [];

	const viewOutlines = useMemo(() => {
		const views: ViewOutline[] | undefined = currentPage?.view_outlines?.views;
		if (!showViewOutlines || !views?.length || !referenceWidth || !referenceHeight) return [];
		const sx = displayedDimensions.width / referenceWidth;
		const sy = displayedDimensions.height / referenceHeight;
		const toX = (x: number) => displayedDimensions.offsetX + x * sx;
		const toY = (y: number) => displayedDimensions.offsetY + y * sy;
		const target = pageBalloons.find(
			(b: Balloon) => b.id === (dragCenter?.id ?? hoveredBalloonId ?? activeBalloonId)
		);
		const targetView = target?.view?.toLowerCase() || null;
		return views.map((outline, index) => {
			const name = outline.view.toLowerCase();
			const keyence = pageBalloons.filter(
				(b: Balloon) =>
					b.measurement_strategy === 'Keyence' && (b.view || '').toLowerCase() === name
			).length;
			const [bx, by] = outline.box;
			return {
				key: `${outline.view}-${index}`,
				color: VIEW_OUTLINE_COLORS[index % VIEW_OUTLINE_COLORS.length],
				points: outline.polygon?.flatMap(([x, y]) => [toX(x), toY(y)]) ?? null,
				labelX: toX(bx),
				labelY: toY(by),
				text: `${outline.label || outline.view}${keyence ? ` · ${keyence} Keyence` : ''}`,
				emphasis: targetView === null ? 'normal' : targetView === name ? 'active' : 'dim',
			};
		});
	}, [
		showViewOutlines,
		currentPage?.view_outlines,
		referenceWidth,
		referenceHeight,
		displayedDimensions,
		pageBalloons,
		dragCenter?.id,
		hoveredBalloonId,
		activeBalloonId,
	]);

	// Selecting another balloon elsewhere (e.g. a table row) closes the popup.
	useEffect(() => {
		if (popupBalloonId && activeBalloonId !== popupBalloonId) {
			setPopupBalloonId(null);
		}
	}, [activeBalloonId, popupBalloonId]);

	useEffect(() => {
		if (!popupBalloonId) return;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setPopupBalloonId(null);
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [popupBalloonId]);

	const popupBalloon = renderBalloonPopup
		? pageBalloons.find((b: Balloon) => b.id === popupBalloonId)
		: undefined;

	// Screen position of the popup: beside the balloon, flipped left or clamped so it
	// stays inside the stage. Recomputed from pan/zoom state so it follows the balloon.
	const contextMenuBalloon = contextMenu
		? pageBalloons.find((b: Balloon) => b.id === contextMenu.balloonId)
		: undefined;

	const popupPosition = useMemo(() => {
		if (!popupBalloon || !referenceWidth || !referenceHeight) return null;
		const centerX =
			displayedDimensions.offsetX +
			(popupBalloon.coordinate_x / referenceWidth) * displayedDimensions.width;
		const centerY =
			displayedDimensions.offsetY +
			(popupBalloon.coordinate_y / referenceHeight) * displayedDimensions.height;
		const screenX = stageOffset.x + centerX * stageScale;
		const screenY = stageOffset.y + centerY * stageScale;
		const halfWidth =
			((computedCircleSize * (popupBalloon.balloon_number >= 100 ? 1.6 : 1)) / 2) *
			stageScale;
		let left = screenX + halfWidth + POPUP_GAP;
		if (left + POPUP_WIDTH > stageSize.width) {
			left = screenX - halfWidth - POPUP_GAP - POPUP_WIDTH;
		}
		left = Math.max(POPUP_GAP, Math.min(left, stageSize.width - POPUP_WIDTH - POPUP_GAP));
		const top = Math.max(
			POPUP_GAP,
			Math.min(screenY - 24, stageSize.height - POPUP_ESTIMATED_HEIGHT - POPUP_GAP)
		);
		return { left, top };
	}, [
		popupBalloon,
		referenceWidth,
		referenceHeight,
		displayedDimensions,
		stageOffset,
		stageScale,
		stageSize,
		computedCircleSize,
	]);

	// Tablet support
	const lastDistRef = useRef<number | null>(null);
	const lastTouchPosRef = useRef<{ x: number; y: number } | null>(null);
	const doubleTapTimeout = useRef<number | null>(null);
	const lastTapTime = useRef<number>(0);
	const lastTapPos = useRef<{ x: number; y: number } | null>(null);
	const defaultScale = 1;
	const defaultOffset = { x: 0, y: 0 };
	const lastCenter = useRef({ x: 0, y: 0 });
	const lastScale = useRef(1);
	const dragStopped = useRef(false);

	const getCenter = (touches) => {
		return {
			x: (touches[0].clientX + touches[1].clientX) / 2,
			y: (touches[0].clientY + touches[1].clientY) / 2
		};
	};

	const getDistance = (touches) => {
		const touch1 = touches[0];
		const touch2 = touches[1];
		return Math.hypot(
			touch2.clientX - touch1.clientX,
			touch2.clientY - touch1.clientY
		);
	};

	const handleTouchStart = useCallback((e: Konva.KonvaEventObject<TouchEvent>) => {
		const touches = e.evt.touches;
		// Pinches fire touchstart per finger; skip double-tap-to-reset for multi-touch.
		if (touches.length > 1) return;

		const currentTime = Date.now();
		const timeDiff = currentTime - lastTapTime.current;
		lastTapTime.current = currentTime;

		// Get tap position
		const touch = touches[0];
		if (!touch) return;

		const stage = e.target.getStage();
		if (!stage) return;

		const pointer = stage.getPointerPosition();
		if (!pointer) return;

		if (lastTapPos.current) {
			const distance = Math.hypot(
				pointer.x - lastTapPos.current.x,
				pointer.y - lastTapPos.current.y
			);

			if (distance > 30) {
				lastTapPos.current = { x: pointer.x, y: pointer.y };
				return;
			}
		}

		lastTapPos.current = { x: pointer.x, y: pointer.y };
		if (timeDiff < 300) {
			if (doubleTapTimeout.current) {
				clearTimeout(doubleTapTimeout.current);
				doubleTapTimeout.current = null;
			}

			setStageScale(defaultScale);
			setStageOffset(defaultOffset);
		} else {
			doubleTapTimeout.current = window.setTimeout(() => {
				doubleTapTimeout.current = null;
			}, 300);
		}
	}, []);


	const handleTouchMove = useCallback((e: Konva.KonvaEventObject<TouchEvent>) => {
		e.evt.preventDefault();
		const stage = e.target.getStage();
		if (!stage) return;

		const touches = e.evt.touches;
		if (touches.length > 2) return;

		const touch1 = touches[0];
		const touch2 = touches[1];


		if (touch1 && touch2 && !stage.isDragging() && dragStopped.current) {
			stage.startDrag();
			dragStopped.current = false;
		}
		if (touch1 && touch2) {

			if (stage.isDragging()) {
				dragStopped.current = true;
				stage.stopDrag();
			}

			const newCenter = getCenter(touches);
			const currentDistance = getDistance(touches);

			if (!lastCenter.current || !lastDistRef.current) {
				lastCenter.current = newCenter;
				lastDistRef.current = currentDistance;
				return;
			}

			const oldScale = stageScale;
			const pointTo = {
				x: (newCenter.x - stageOffset.x) / oldScale,
				y: (newCenter.y - stageOffset.y) / oldScale,
			};

			// Cap per-frame scale change; a finger drifting off the stage can spike currentDistance before touchend fires.
			const rawScaleDelta = currentDistance / lastDistRef.current;
			const scaleDelta = Math.min(Math.max(rawScaleDelta, 0.5), 2);
			const newScale = stageScale * scaleDelta;
			const clampedScale = Math.min(Math.max(newScale, 0.5), 5);

			const dx = newCenter.x - lastCenter.current.x;
			const dy = newCenter.y - lastCenter.current.y;

			const newPos = {
				x: newCenter.x - pointTo.x * clampedScale + dx,
				y: newCenter.y - pointTo.y * clampedScale + dy,
			};

			setStageScale(clampedScale);
			setStageOffset(newPos);

			lastDistRef.current = currentDistance;
			lastCenter.current = newCenter;

		} else if (touch1) {

			// Single-finger drag (pan)
			const touchPos = { x: touch1.clientX, y: touch1.clientY };

			if (lastTouchPosRef.current) {
				const dx = touchPos.x - lastTouchPosRef.current.x;
				const dy = touchPos.y - lastTouchPosRef.current.y;

				setStageOffset((prevOffset) => ({
					x: prevOffset.x + (isNaN(dx) ? 0 : dx),
					y: prevOffset.y + (isNaN(dy) ? 0 : dy),
				}));
			}

			lastTouchPosRef.current = touchPos;
		}
	},
		[stageOffset, stageScale]
	);

	const handleTouchEnd = useCallback(() => {
		lastDistRef.current = null;
		lastTouchPosRef.current = null;
		lastScale.current = stageScale;
	}, []);

	const renderBottomPageSelector = () => (
		(ballooningPages?.length > 1 &&
			<div >
				<Button
					variant="filled"
					style={{
						width: '40px',
						textAlign: 'center',
						color: 'orange',
						borderRadius: '5px',
						padding: '5px',
						margin: '10px',
						backgroundColor: 'transparent'
					}}
					onClick={() => currentPage && currentPage.page_number > 1 && setPageNumber(currentPage.page_number - 1)}>
					<CaretLeftOutlined />
				</Button>
				{ballooningPages.map((page) => (
					<Button
						key={page.page_number}
						onClick={() => setPageNumber(page.page_number)}
						style={{
							width: '40px',
							textAlign: 'center',
							color: 'orange',
							boxShadow: 'none',
							borderRadius: '5px',
							border:
								page.page_number === pageNumber
									? '2px solid #1677ff'
									: '2px solid transparent',
							padding: '5px',
							margin: '5px',
							backgroundColor: 'transparent',
						}}

					>
						<div
							style={{
								fontSize: '12px',
								fontWeight: 'bold',
							}}
						>
							{page.page_number}
						</div>
					</Button>
				))}
				<Button
					style={{
						width: '40px',
						textAlign: 'center',
						color: 'orange',
						borderRadius: '5px',
						padding: '5px',
						margin: '10px',
						backgroundColor: 'transparent'
					}}
					onClick={() => currentPage && currentPage.page_number < ballooningPages?.length && setPageNumber(currentPage.page_number + 1)}>
					<CaretRightOutlined />
				</Button>
			</div>)
	);

	return (
		<>
			<div
				ref={containerRef}
				style={{
					width: '100%',
					height: '100%',
					overflow: 'hidden',
					backgroundColor: '#ddd',
					position: 'relative',
					display: 'flex',
				}}
			>
				{showLeftPageBar && !isTabletView && (
					<div className="w-[160px] min-w-[160px] shrink-0 overflow-y-auto overflow-x-hidden bg-white/90 p-2 border-r border-gray-300 flex flex-col gap-2 min-h-0">
						<BallooningPages
							pageNumber={pageNumber}
							setPageNumber={setPageNumber}
							ballooningPages={ballooningPages}
							hoveringSidebar={false}
						/>
					</div>
				)}

				<div ref={drawingRef} data-testid="ballooning-stage" style={{ width: '100%', height: '100%', position: 'relative' }}>
					{getBallooningProject.isLoading || !getBallooningProject.data ? (
						<Loading loading={true} />
					) : !pageExists ? (
						<div className="flex items-center justify-center w-full h-full">
							<Typography.Text type="secondary">
								Page {pageNumber} does not exist in this document.
							</Typography.Text>
						</div>
					) : imageLoadState === 'loading' ? (
						<Loading loading={true} />
					) : !canRenderStage ? (
						<div className="flex items-center justify-center w-full h-full">
							<Typography.Text type="secondary">
								{imageLoadState === 'missing'
									? 'Drawing image is not available.'
									: 'Drawing image could not be loaded.'}
							</Typography.Text>
						</div>
					) : (
						<>
							{imageUnavailable && (
								<div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
									<Typography.Text type="secondary">
										{imageLoadState === 'missing'
											? 'Drawing image is not available.'
											: 'Drawing image could not be loaded.'}
									</Typography.Text>
								</div>
							)}
							<Stage
								data-testid="ballooning-stage"
								ref={stageRef}
								width={stageSize.width}
								height={stageSize.height}
								scaleX={stageScale}
								scaleY={stageScale}
								x={stageOffset.x}
								y={stageOffset.y}
								onWheel={handleWheel}
								onMouseDown={handleStageMouseDown}
								onMouseMove={handleStageMouseMove}
								onMouseUp={handleStageMouseUp}
								onContextMenu={handleContextMenu}
								onTouchMove={handleTouchMove}
								onTouchEnd={handleTouchEnd}
								onTouchStart={handleTouchStart}
								style={{ background: 'transparent', cursor: 'default' }}
							>
								<Layer>
									{konvaImage ? (
										<KonvaImage
											id="konvaImage"
											image={konvaImage}
											x={displayedDimensions.offsetX}
											y={displayedDimensions.offsetY}
											width={displayedDimensions.width}
											height={displayedDimensions.height}
											perfectDrawEnabled={false}
											shadowEnabled={true}
											shadowColor={'black'}
											shadowBlur={40}
											shadowOpacity={0.2}
											shadowOffsetX={20}
											shadowOffsetY={20}
										/>
									) : (
										<Rect
											id="konvaImage"
											x={displayedDimensions.offsetX}
											y={displayedDimensions.offsetY}
											width={displayedDimensions.width}
											height={displayedDimensions.height}
											fill="#e8e8e8"
											stroke="#bfbfbf"
											strokeWidth={1}
										/>
									)}
									{viewOutlines.length > 0 && (
										<Group listening={false} data-testid="view-outlines">
											{viewOutlines.map((outline) => (
												<Group
													key={outline.key}
													opacity={outline.emphasis === 'dim' ? 0.3 : 1}
												>
													{outline.points && (
														<Line
															points={outline.points}
															closed
															stroke={outline.color}
															strokeWidth={Math.max(
																computedStrokeWidth * (outline.emphasis === 'active' ? 0.9 : 0.5),
																1
															)}
															fill={`${outline.color}${outline.emphasis === 'active' ? '40' : '22'}`}
														/>
													)}
													<Text
														x={outline.labelX}
														y={outline.labelY - computedCircleSize * 0.75}
														text={outline.points ? outline.text : `${outline.text} (not traced)`}
														fontSize={Math.max(computedCircleSize * 0.55, 8)}
														fontStyle="600"
														fill={outline.color}
													/>
												</Group>
											))}
										</Group>
									)}
									{featureOverlay && (
										<Group listening={false}>
											<Rect
												x={featureOverlay.box.x}
												y={featureOverlay.box.y}
												width={featureOverlay.box.w}
												height={featureOverlay.box.h}
												stroke={featureOverlay.color}
												strokeWidth={Math.max(computedStrokeWidth * 0.5, 1)}
												dash={[computedStrokeWidth * 1.5, computedStrokeWidth]}
												fill="rgba(22,119,255,0.06)"
											/>
											<Line
												points={[
													featureOverlay.center.x,
													featureOverlay.center.y,
													featureOverlay.anchor.x,
													featureOverlay.anchor.y,
												]}
												stroke={featureOverlay.color}
												strokeWidth={Math.max(computedStrokeWidth * 0.5, 1)}
											/>
										</Group>
									)}
									{leaderLines.length > 0 && (
										<Group listening={false} data-testid="leader-lines">
											{leaderLines.map((line) => (
												<Line
													key={line.id}
													points={line.points}
													stroke={line.color}
													strokeWidth={computedStrokeWidth * 0.3}
												/>
											))}
										</Group>
									)}
										{pageBalloons.map((balloon) => {
												const centerX =
													displayedDimensions.offsetX +
													(balloon.coordinate_x / referenceWidth) *
													displayedDimensions.width;
												const centerY =
													displayedDimensions.offsetY +
													(balloon.coordinate_y / referenceHeight) *
													displayedDimensions.height;
												return (
													<Group
														key={balloon.id}
														x={centerX}
														y={centerY}
														draggable={editable && !disableDragging && (!allowedEditableBalloonId || allowedEditableBalloonId === balloon.id)}
														onDragStart={(e) => {
															const canvas = getCanvasElement();
															if (canvas) canvas.style.cursor = 'grabbing';
															setPopupBalloonId(null);
														}}
														onClick={(e: Konva.KonvaEventObject<MouseEvent>) => {
															if (renderBalloonPopup && e.evt.button === 0) {
																setPopupBalloonId(balloon.id);
															}
														}}
														onTap={() => {
															if (renderBalloonPopup) setPopupBalloonId(balloon.id);
														}}
														onContextMenu={(e: Konva.KonvaEventObject<PointerEvent>) =>
															handleBalloonContextMenu(balloon, e)
														}
														onDragMove={
															editable
																? (e: Konva.KonvaEventObject<DragEvent>) => {
																	handleBalloonDragMove(e);
																	// The group moves on the canvas before the balloon's
																	// coordinates change; the leader line follows the group.
																	setDragCenter({
																		id: balloon.id,
																		x: e.target.x(),
																		y: e.target.y(),
																	});
																}
																: undefined
														}
														onDragEnd={
															editable
																? (e) => {
																	setDragCenter(null);
																	handleCircleDragEnd(balloon, e);
																}
																: undefined
														}
														onMouseEnter={(e) => {
															const canvas = getCanvasElement();
															if (canvas) canvas.style.cursor = 'pointer';
															setHoveredBalloonId(balloon.id);
														}}
														onMouseLeave={(e) => {
															const canvas = getCanvasElement();
															if (canvas) canvas.style.cursor = 'default';
															setHoveredBalloonId(null);
														}}
														onMouseDown={() => {
															setActiveBalloonId(balloon.id);
														}}
													>
														{(() => {
															const unreviewed =
																showReviewAids && isUnreviewedAutoBalloon(balloon);
															const strokeColor = balloonStrokeColor(balloon);
															return (
																<>
																	<Rect
																		x={- (computedCircleSize * (balloon.balloon_number >= 100 ? 1.6 : 1)) / 2}
																		y={-computedCircleSize / 2}
																		width={computedCircleSize * (balloon.balloon_number >= 100 ? 1.6 : 1)}
																		height={computedCircleSize}
																		cornerRadius={computedCircleSize / 2}
																		stroke={strokeColor}
																		strokeWidth={computedStrokeWidth}
																		dash={
																			unreviewed
																				? [computedStrokeWidth * 2, computedStrokeWidth * 1.2]
																				: undefined
																		}
																		fill="rgba(255,255,255,1)"
																		shadowColor={
																			activeBalloonId === balloon.id
																				? strokeColor
																				: 'rgba(0,0,0,0)'
																		}
																		shadowBlur={
																			activeBalloonId === balloon.id
																				? computedStrokeWidth * 2.5
																				: 0
																		}
																	/>
																	<Text
																		text={balloon.balloon_number.toString()}
																		x={- (computedCircleSize * (balloon.balloon_number >= 100 ? 1.6 : 1)) / 2}
																		y={-computedCircleSize / 2}
																		width={computedCircleSize * (balloon.balloon_number >= 100 ? 1.6 : 1)}
																		height={computedCircleSize}
																		fontSize={computedCircleSize * 0.7}
																		fill={strokeColor}
																		fontStyle="500"
																		align="center"
																		verticalAlign="middle"
																	/>
																</>
															);
														})()}
													</Group>
												);
											})}
									</Layer>
								</Stage>
							{popupBalloon && popupPosition && renderBalloonPopup && (
								<div
									style={{
										position: 'absolute',
										left: popupPosition.left,
										top: popupPosition.top,
										width: POPUP_WIDTH,
										maxHeight: `calc(100% - ${POPUP_GAP * 2}px)`,
										overflowY: 'auto',
										zIndex: 1001,
										background: '#fff',
										borderRadius: 8,
										padding: 12,
										boxShadow: '0 6px 16px rgba(0,0,0,0.12), 0 3px 6px -4px rgba(0,0,0,0.12)',
									}}
									onMouseDown={(e) => e.stopPropagation()}
									onWheel={(e) => e.stopPropagation()}
								>
									{renderBalloonPopup(popupBalloon, closePopup)}
								</div>
							)}
							{contextMenuBalloon && contextMenu && onDeleteBalloon && (
								<div
									style={{
										position: 'absolute',
										left: contextMenu.left,
										top: contextMenu.top,
										width: CONTEXT_MENU_WIDTH,
										zIndex: 1002,
										borderRadius: 8,
										overflow: 'hidden',
										boxShadow: '0 6px 16px rgba(0,0,0,0.12), 0 3px 6px -4px rgba(0,0,0,0.12)',
									}}
									onMouseDown={(e) => e.stopPropagation()}
									onContextMenu={(e) => e.preventDefault()}
									data-testid="balloon-context-menu"
								>
									<Menu
										selectable={false}
										items={[
											{
												key: 'delete',
												danger: true,
												icon: <DeleteOutlined />,
												label: `Delete balloon ${contextMenuBalloon.balloon_number}`,
											},
										]}
										onClick={({ key }) => {
											setContextMenu(null);
											if (key === 'delete') onDeleteBalloon(contextMenuBalloon);
										}}
									/>
								</div>
							)}
							<div
								style={{
									position: "absolute",
									bottom: 0,
									width: "100%",
									display: "flex",
									justifyContent: "center",
									padding: "10px",
									background: "transparent",
									zIndex: "1000"
								}}
							>
								{isTabletView && renderBottomPageSelector()}
							</div>
						</>
					)}
				</div>
			</div>

			<Modal
				title="Renumber Balloons?"
				visible={confirmModalVisible}
				onOk={handleModalOk}
				onCancel={handleModalCancel}
				okText="Renumber"
				cancelText="Do not renumber"
				centered
			>
				<p>
					This balloon is not the last in sequence. Would you like to renumber
					subsequent balloons to prevent a gap?
				</p>
			</Modal>
		</>
	);

};

export default BallooningViewer;
